export interface Wod {
  id: string;
  name: string;
  date?: string | null;
  description?: string | null;
  completedOnDate?: boolean | null;
  isFavorited?: boolean | null;
  cover?: {uri?: string | null} | null;
  video?: {uri?: string | null} | null;
  comments?: {total?: number | null} | null;
}

// A query espera YYYY-MM-DD. Usar toISOString() aqui seria um bug sutil: ela
// converte para UTC, entao no fuso do Brasil toda data antes das 21h vira o
// dia seguinte e o aluno veria o treino errado.
export const toDateParam = (data: Date): string => {
  const ano = data.getFullYear();
  const mes = `${data.getMonth() + 1}`.padStart(2, '0');
  const dia = `${data.getDate()}`.padStart(2, '0');

  return `${ano}-${mes}-${dia}`;
};

export const addDays = (data: Date, dias: number): Date => {
  const nova = new Date(data);
  nova.setDate(nova.getDate() + dias);

  return nova;
};

export const isSameDay = (a: Date, b: Date): boolean => toDateParam(a) === toDateParam(b);

const DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

export const dayLabel = (data: Date, hoje: Date = new Date()): string => {
  if (isSameDay(data, hoje)) return 'Hoje';
  if (isSameDay(data, addDays(hoje, -1))) return 'Ontem';
  if (isSameDay(data, addDays(hoje, 1))) return 'Amanhã';

  const dia = `${data.getDate()}`.padStart(2, '0');
  const mes = `${data.getMonth() + 1}`.padStart(2, '0');

  return `${DIAS[data.getDay()]}, ${dia}/${mes}`;
};

export const acaoDeFavorito = (wod: Wod): 'add' | 'remove' => (wod.isFavorited ? 'remove' : 'add');

// A conclusao e gravada por data. `wods(date:)` devolve o treino que a rotina
// escala para o dia consultado, e o campo `date` do wod e esse mesmo dia — nao a
// data de cadastro do workout_group. Gravar por ele, e nao por hoje, e o que
// mantem o historico correto quando o aluno navega para outro dia; se a API
// vier sem data legivel, o dia exibido na tela e a mesma informacao.
export const dataDeConclusao = (wod: Wod, diaExibido: Date): string => {
  const bruta = wod.date?.trim().slice(0, 10);

  return bruta && /^\d{4}-\d{2}-\d{2}$/.test(bruta) ? bruta : toDateParam(diaExibido);
};

// programFilter vem como String; o painel pode gravar um id so ou varios
// separados por virgula. Entradas vazias ou nao numericas sao descartadas para
// nao mandar lixo como programIds e zerar a lista sem motivo.
export const parseProgramIds = (filtro?: string | null): string[] => {
  if (!filtro) return [];

  return filtro
    .split(',')
    .map((parte) => parte.trim())
    .filter((parte) => parte.length > 0 && /^\d+$/.test(parte));
};

export interface ExerciseWorkout {
  id: string;
  repetitions?: string | null;
  load?: string | null;
  rounds?: string | null;
  timeText?: string | null;
  restTime?: number | null;
  notes?: string | null;
  exercise?: {id: string; name: string} | null;
}

// "3 x 10 · 20kg · 60s de descanso" a partir do que o professor preencheu.
// O professor preenche o que quiser, entao o resumo ignora o que ficou vazio.
export const exerciseSummary = (item: ExerciseWorkout): string =>
  [
    item.rounds && item.repetitions ? `${item.rounds} x ${item.repetitions}` : item.repetitions || item.rounds,
    item.load,
    item.timeText,
    item.restTime ? `${item.restTime}s de descanso` : null,
  ]
    .filter((parte) => Boolean(parte) && String(parte).trim() !== '')
    .join(' · ');
